import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { 
  Home,
  Calculator,
  FileText,
  HelpCircle,
  Search,
  ArrowLeft
} from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';

export function NotFoundPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');

  const popularPages = [
    {
      title: 'Dashboard',
      description: 'View your quotes and activity',
      href: '/dashboard',
      icon: Home,
      color: 'text-blue-500'
    },
    {
      title: 'New Quote',
      description: 'Create a new packaging quote',
      href: '/calculator',
      icon: Calculator,
      color: 'text-green-500'
    },
    {
      title: 'My Quotes',
      description: 'View all your saved quotes',
      href: '/quotes',
      icon: FileText,
      color: 'text-purple-500'
    },
    {
      title: 'Help Center',
      description: 'Get help and support',
      href: '/contact',
      icon: HelpCircle,
      color: 'text-orange-500'
    }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search or help page
      navigate(`/contact?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <PublicLayout>
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-4xl w-full">
          {/* 404 Hero */}
          <div className="text-center mb-12">
            <div className="mb-6">
              <h1 className="text-9xl font-bold text-primary mb-4">404</h1>
              <h2 className="text-3xl font-bold mb-2">Page Not Found</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Sorry, we couldn&apos;t find the page you&apos;re looking for. 
                It might have been moved, deleted, or the URL might be incorrect.
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-md mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for pages..."
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </form>
          </div>

          {/* Popular Pages */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold text-center mb-6">
              Popular Pages
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {popularPages.map((page) => (
                <Link key={page.href} to={page.href} className="block">
                  <Card className="hover:border-primary transition-colors h-full">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 ${page.color}`}>
                          <page.icon className="h-8 w-8" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">{page.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {page.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            <Button asChild className="w-full sm:w-auto">
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Back to Home
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/contact">
                <HelpCircle className="h-4 w-4 mr-2" />
                Contact Support
              </Link>
            </Button>
          </div>

          {/* Help Text */}
          <div className="mt-12 text-center">
            <Card className="bg-muted/50">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  If you believe this is an error or need assistance, please{' '}
                  <Link to="/contact" className="text-primary hover:underline font-medium">
                    contact our support team
                  </Link>
                  . We&apos;re here to help!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
